import { Badge, Btn, Card, Dot, I, StateBadge } from "../../ui";
import type { DeviceDTO, PairingCodeDTO } from "../../../api/contract";
import { deviceStateKey, formatRelative } from "./utils";
import { isExtensionOutdated } from "./version";

interface DeviceCounts {
  online: number;
  offline: number;
  revoked: number;
}

interface DevicesCardProps {
  currentVersion: string | null;
  activePairings: PairingCodeDTO[];
  counts: DeviceCounts;
  devices: DeviceDTO[];
  loading: boolean;
  loadErr: string | null;
  selectedIds: Set<string>;
  revokeBusy: boolean;
  onNewPairing: () => void;
  onOpenDevice: (id: string) => void;
  onRevoke: (id: string) => void;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  onBulkRevoke: () => void;
  onClearSelection: () => void;
  onOpenUpdateGuide: () => void;
}

export function DevicesCard({
  currentVersion,
  activePairings,
  counts,
  devices,
  loading,
  loadErr,
  selectedIds,
  revokeBusy,
  onNewPairing,
  onOpenDevice,
  onRevoke,
  onToggleSelect,
  onToggleSelectAll,
  onBulkRevoke,
  onClearSelection,
  onOpenUpdateGuide,
}: DevicesCardProps) {
  const visible = devices.filter((d) => !d.revokedAt);
  const allSelected = visible.length > 0 && visible.every((d) => selectedIds.has(d.id));
  const selectedCount = selectedIds.size;

  return (
    <Card noPad>
      <div style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: 16, borderBottom: "1px solid var(--line)", flexWrap: "wrap" }}>
        <div className="col gap-4">
          <div className="card-title">Estensioni abbinate</div>
          <div className="card-sub">
            {counts.online} {counts.online === 1 ? "accesa" : "accese"} adesso · {counts.offline} {counts.offline === 1 ? "spenta" : "spente"}
            {counts.revoked > 0 && ` · ${counts.revoked} revocate (nascoste)`}
          </div>
        </div>
        <div className="row gap-8">
          {activePairings.length > 0 && (
            <Badge kind="info"><Dot kind="info" />{activePairings.length} codici attivi</Badge>
          )}
        </div>
        <div className="grow"></div>
        {selectedCount > 0 && (
          <div className="row gap-8" style={{ alignItems: "center" }}>
            <span className="muted" style={{ fontSize: 12 }}>{selectedCount} selezionate</span>
            <Btn size="sm" variant="ghost" disabled={revokeBusy} onClick={onClearSelection}>
              Deseleziona
            </Btn>
            <Btn
              size="sm"
              variant="danger"
              disabled={revokeBusy}
              onClick={onBulkRevoke}
              title="Revoca tutte le estensioni selezionate"
            >
              {revokeBusy ? "revoca…" : `Revoca selezionate (${selectedCount})`}
            </Btn>
          </div>
        )}
        <Btn variant="primary" icon={I.plus} onClick={onNewPairing}>
          Nuovo abbinamento
        </Btn>
      </div>

      {loadErr && (
        <div style={{ padding: 16, color: "var(--danger)", fontSize: 13 }}>{loadErr}</div>
      )}

      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 36 }}>
              <input
                type="checkbox"
                checked={allSelected}
                disabled={visible.length === 0 || revokeBusy}
                onChange={onToggleSelectAll}
                title={allSelected ? "Deseleziona tutte" : "Seleziona tutte"}
                aria-label="Seleziona tutte"
              />
            </th>
            <th>Estensione</th>
            <th>Stato</th>
            <th style={{ textAlign: "right" }}>Ricerche fatte</th>
            <th>Ultimo accesso</th>
            <th>Abbinata</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {!loading && visible.length === 0 && (
            <tr>
              <td colSpan={7} style={{ padding: 24, color: "var(--text-2)", textAlign: "center", fontSize: 13 }}>
                Nessuna estensione abbinata. Segui la guida di installazione e genera un codice con «Nuovo abbinamento».
              </td>
            </tr>
          )}
          {visible.map((d) => {
            const checked = selectedIds.has(d.id);
            return (
              <tr
                key={d.id}
                className="session-row selectable"
                onClick={() => onOpenDevice(d.id)}
                style={checked ? { background: "var(--bg-2)" } : undefined}
              >
                <td onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={revokeBusy}
                    onChange={() => onToggleSelect(d.id)}
                    aria-label={`Seleziona ${d.label}`}
                  />
                </td>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div className="avatar" style={{ width: 26, height: 26, fontSize: 10 }}>{d.label.substring(0, 2).toUpperCase()}</div>
                    <div>
                      <div style={{ fontWeight: 500 }}>
                        {d.label}
                        {d.lastVersion && (
                          <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>v{d.lastVersion}</span>
                        )}
                      </div>
                      {isExtensionOutdated(d.lastVersion, currentVersion) && (
                        <div className="row gap-8" style={{ marginTop: 4, fontSize: 12 }}>
                          <Badge kind="warn" title={`Versione disponibile: ${currentVersion}`}>Da aggiornare</Badge>
                          <a href="#aggiornare-estensione" onClick={(e) => {
                            e.stopPropagation();
                            onOpenUpdateGuide();
                          }}>Come aggiornare</a>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td>
                  <StateBadge state={deviceStateKey(d)} />
                  {d.online && (
                    <div
                      className="muted"
                      style={{ fontSize: 11, marginTop: 4 }}
                      title={d.searching ? undefined : "Chrome è aperto ma la ricerca è ferma: apri l’estensione e premi «Prepara SISTER e avvia»."}
                    >
                      {d.searching ? "Ricerca SISTER attiva" : "Ricerca SISTER ferma"}
                    </div>
                  )}
                </td>
                <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{d.tasksDone}</td>
                <td style={{ fontSize: 12, color: "var(--text-2)" }}>{formatRelative(d.lastSeenAt)}</td>
                <td style={{ fontSize: 12, color: "var(--text-2)" }}>{formatRelative(d.createdAt)}</td>
                <td style={{ textAlign: "right" }}>
                  <Btn
                    size="sm"
                    variant="ghost"
                    disabled={revokeBusy}
                    title="Scollega questa estensione: non potrà più fare ricerche"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRevoke(d.id);
                    }}
                  >
                    Revoca
                  </Btn>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}
